import { z } from 'zod';
import { getAlpha2Codes } from 'i18n-iso-countries';
import retry from 'async-retry';
import { diary, enable } from 'diary';
import { get } from 'radash';
import { murmur3 } from 'murmurhash-js';

const Query = z.record(z.string(), z.string());

const Page = z.object({
  path: z.string(),
  query: Query,
  referrer: z.string(),
  search: z.string(),
  url: z.string()
});

const SessionStatistics = z.object({
  id: z.string(),
  isReturningVisitor: z.boolean(),
  landingPage: Page,
  count: z.number(),
  activeSessionLength: z.number(),
  averageSessionLength: z.number()
});

const Alpha2Code = z.enum(['unknown', ...Object.keys(getAlpha2Codes())]);
const GeoLocation = z.object({
  coordinates: z.object({
    latitude: z.number(),
    longitude: z.number()
  }).optional(),
  city: z.string().optional(),
  postalCode: z.string().optional(),
  region: z.string().optional(),
  regionCode: z.string().optional(),
  country: z.string().optional(),
  countryCode: Alpha2Code.optional(),
  continent: z.string().optional(),
  timezone: z.string().optional()
});

const JsonLiteral = z.lazy(() => z.union([z.string(), z.number(), z.boolean(), z.null(), z.symbol(), z.array(JsonLiteral), JsonLiteralObject]));
const JsonLiteralObject = z.record(z.string(), z.union([JsonLiteral, z.array(JsonLiteral)]));
const Json = z.lazy(() => z.union([JsonLiteral, z.array(Json), z.record(Json)]));
const JsonObject = z.record(Json);

const Profile = z.object({
  id: z.string(),
  stableId: z.string(),
  random: z.number(),
  audiences: z.array(z.string()),
  traits: JsonObject,
  location: GeoLocation,
  session: SessionStatistics
});

const RequestBodyOptions = z.object({
  features: z.array(z.string()).optional()
});

const SelectedVariantInfo = z.object({
  experienceId: z.string(),
  variantIndex: z.number(),
  variants: z.record(z.string()),
  sticky: z.boolean().optional().default(false)
});

const withResponseEnvelope = dataSchema => z.object({
  data: dataSchema,
  message: z.string(),
  error: z.boolean().nullable()
});

let ChangeTypes = /*#__PURE__*/function (ChangeTypes) {
  ChangeTypes["Variable"] = "Variable";
  return ChangeTypes;
}({});
const ChangeBase = z.object({
  key: z.string(),
  type: z.nativeEnum(ChangeTypes),
  meta: z.object({
    experienceId: z.string(),
    variantIndex: z.number()
  })
});
const VariableChange = ChangeBase.extend({
  type: z.literal(ChangeTypes.Variable),
  value: z.union([z.string(), JsonObject])
});
const Change = z.discriminatedUnion('type', [VariableChange]);

const CreateProfileRequestBody = z.object({
  events: z.array(z.unknown()).min(1),
  options: RequestBodyOptions.optional()
});
const CreateProfileResponse = withResponseEnvelope(z.object({
  profile: Profile,
  experiences: z.array(SelectedVariantInfo),
  changes: z.array(Change)
}));

const UpdateProfileRequestBody = z.object({
  events: z.array(z.unknown()).min(1),
  options: RequestBodyOptions.optional()
});
const UpdateProfileResponse = withResponseEnvelope(z.object({
  profile: Profile,
  experiences: z.array(SelectedVariantInfo),
  changes: z.array(Change)
}));

const UpsertManyProfilesRequestBody = z.object({
  events: z.array(z.unknown()).min(1),
  options: RequestBodyOptions.optional()
});
const UpsertManyProfilesResponse = withResponseEnvelope(z.object({
  profiles: z.array(Profile).optional()
}));

const Campaign = z.object({
  name: z.string().optional(),
  source: z.string().optional(),
  medium: z.string().optional(),
  term: z.string().optional(),
  content: z.string().optional()
});

const PageviewProperties = z.object({
  path: z.string(),
  query: Query,
  referrer: z.string(),
  search: z.string(),
  title: z.string(),
  url: z.string(),
  category: z.string().optional()
}).catchall(Json);

const Pageview = 'page';
const Track = 'track';
const Identify = 'identify';
const Screen = 'screen';
const Group = 'group';
const Alias = 'alias';
const Component = 'component';

const LEGACY_ANONYMOUS_ID = '__anon_id';
const ANONYMOUS_ID = '__nt_anonymous_id__';
const DEBUG_FLAG = '__nt_debug__';
const PROFILE_FALLBACK_CACHE = '__nt_profile__';
const EXPERIENCES_FALLBACK_CACHE = '__nt_experiences__';
const PROFILE_CHANGE = 'profile-change';
const PROFILE_RESET = 'profile-reset';
const CONSENT = '__nt-consent__';
const EMPTY_MERGE_ID = 'nt:empty-merge-id';
const NINETAILED_ANONYMOUS_ID_COOKIE = 'ntaid';

class Logger {
  constructor() {
    this.name = '@ninetailed/experience.js';
    this.diary = void 0;
    this.sinks = [];
    this.diary = diary(this.name, this.onLogEvent.bind(this));
    enable(this.name);
  }
  addSink(sink) {
    this.sinks = [...this.sinks.filter(existingSink => existingSink.name !== sink.name), sink];
  }
  removeSink(name) {
    this.sinks = this.sinks.filter(sink => sink.name !== name);
  }
  removeSinks() {
    this.sinks = [];
  }
  debug(message, ...args) {
    this.diary.debug(message, ...args);
  }
  info(message, ...args) {
    this.diary.info(message, ...args);
  }
  log(message, ...args) {
    this.diary.log(message, ...args);
  }
  warn(message, ...args) {
    this.diary.warn(message, ...args);
  }
  error(message, ...args) {
    this.diary.error(message, ...args);
  }
  fatal(message, ...args) {
    this.diary.fatal(message, ...args);
  }
  onLogEvent(event) {
    this.sinks.forEach(sink => {
      sink.ingest(event);
    });
  }
}
const logger = new Logger();

const fetchTimeout = async (url, init, {
  fetchImpl: _fetchImpl = fetch
} = {}) => {
  const {
    timeout,
    onTimeout
  } = init;
  const controller = new AbortController();
  const id = setTimeout(() => {
    if (typeof onTimeout === 'function') {
      logger.error(new Error('Ninetailed Request timed out.'));
    }
    controller.abort();
  }, timeout);
  const response = await _fetchImpl(url, Object.assign({}, init, {
    signal: controller.signal
  }));
  clearTimeout(id);
  return response;
};

const GetProfileResponse = withResponseEnvelope(z.object({
  profile: Profile,
  experiences: z.array(SelectedVariantInfo),
  changes: z.array(Change)
}));

const BASE_URL = 'https://experience.ninetailed.co';
const FEATURES = {
  IP_ENRICHMENT: 'ip-enrichment',
  LOCATION: 'location'
};
const DEFAULT_ENVIRONMENT = 'main';
class HttpError extends Error {
  constructor(message, status = 500) {
    super(message);
    this.status = status;
    Object.setPrototypeOf(this, HttpError.prototype);
  }
}
class NinetailedApiClient {
  constructor({
    clientId,
    environment = DEFAULT_ENVIRONMENT,
    url = BASE_URL,
    fetchImpl = fetch
  }) {
    this.clientId = void 0;
    this.environment = void 0;
    this.url = void 0;
    this.fetchImpl = void 0;
    this.constructOptions = options => {
      const bodyOptions = {};
      if (options.enabledFeatures && Array.isArray(options.enabledFeatures) && options.enabledFeatures.length > 0) {
        bodyOptions.features = options.enabledFeatures;
      }
      return bodyOptions;
    };
    this.clientId = clientId;
    this.environment = environment;
    this.url = url;
    this.fetchImpl = fetchImpl;
  }
  logRequestError(error, {
    requestName
  }) {
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        logger.warn(`${requestName} request aborted due to network issues. This request is not retryable.`);
      } else {
        logger.error(`${requestName} request failed with error: [${error.name}] ${error.message}`);
      }
    }
  }
  async upsertProfile({
    profileId,
    events
  }, options) {
    if (!profileId) {
      return this.createProfile({
        events
      }, options);
    } else {
      return this.updateProfile({
        profileId: profileId,
        events
      }, options);
    }
  }
  retryRequest(request, name, options) {
    var _options$retries, _options$minRetryTime;
    return retry(async function (bail) {
      try {
        const response = await request;
        if (response.status === 503) {
          throw new HttpError(`${name} request failed with status: "[${response.status}] ${response.statusText}".`, 503);
        }
        if (!response.ok) {
          bail(new Error(`${name} request failed with status: "[${response.status}] ${response.statusText} - traceparent: ${response.headers.get('traceparent')}". This request is not retryable`));
          return null;
        }
        logger.debug(`${name} response: `, response);
        return response;
      } catch (error) {
        if (error instanceof HttpError && error.status === 503) {
          throw error;
        }
        if (error instanceof Error) {
          bail(error);
        } else {
          bail(new Error(`${name} request failed with an unknown error. This request is not retryable.`));
        }
        return null;
      }
    }, {
      retries: (_options$retries = options.retries) != null ? _options$retries : 1,
      minTimeout: (_options$minRetryTime = options.minRetryTimeout) != null ? _options$minRetryTime : 0,
      onRetry: (error, attempt) => logger.error(`${error.message} Retrying (attempt ${attempt}).`)
    });
  }
  makeProfileMutationRequest(url, payload, name, options) {
    return this.retryRequest(fetchTimeout(this.constructUrl(url, options), {
      method: 'POST',
      headers: this.constructHeaders(options),
      body: JSON.stringify(payload),
      timeout: options.timeout || 3000
    }, {
      fetchImpl: this.fetchImpl
    }), name, options);
  }
  async getProfile(id, options = {}) {
    const requestName = 'Get Profile';
    logger.info(`Sending ${requestName} request.`);
    try {
      const response = await this.retryRequest(fetchTimeout(this.constructUrl(`/v2/organizations/${this.clientId}/environments/${this.environment}/profiles/${id}`, options), {
        method: 'GET',
        timeout: options.timeout || 3000
      }, {
        fetchImpl: this.fetchImpl
      }), 'Get Profile', options);
      const {
        data
      } = GetProfileResponse.parse(await response.json());
      logger.debug(`${requestName} request succesfully completed.`);
      return data;
    } catch (error) {
      this.logRequestError(error, {
        requestName
      });
      throw error;
    }
  }

  /**
   * Creates a profile and returns it.
   * Use the given profileId for subsequent update requests.
   * The events will be used to aggregate the new Profile state.
   */
  async createProfile({
    events
  }, options = {}) {
    const requestName = 'Create Profile';
    logger.info(`Sending ${requestName} request.`);
    const body = {
      events,
      options: this.constructOptions(options)
    };
    logger.debug(`${requestName} request Body: `, body);
    try {
      const response = await this.makeProfileMutationRequest(`/v2/organizations/${this.clientId}/environments/${this.environment}/profiles`, body, 'Create Profile', options);
      const {
        data
      } = CreateProfileResponse.parse(await response.json());
      logger.debug(`${requestName} request succesfully completed.`);
      return data;
    } catch (error) {
      this.logRequestError(error, {
        requestName
      });
      throw error;
    }
  }

  /**
   * Updates a profile with the given profileId.
   * The events will be used to aggregate the new Profile state.
   */
  async updateProfile({
    profileId,
    events
  }, options = {}) {
    const requestName = 'Update Profile';
    logger.info(`Sending ${requestName} request.`);
    const body = {
      events,
      options: this.constructOptions(options)
    };
    logger.debug(`${requestName} request Body: `, body);
    try {
      const response = await this.makeProfileMutationRequest(`/v2/organizations/${this.clientId}/environments/${this.environment}/profiles/${profileId}`, body, requestName, options);
      const {
        data
      } = UpdateProfileResponse.parse(await response.json());
      logger.debug(`${requestName} request successfully completed.`);
      return data;
    } catch (error) {
      this.logRequestError(error, {
        requestName
      });
      throw error;
    }
  }

  /**
   * Sends multiple events to the Ninetailed API.
   * Every events needs to have a anonymous ID.
   * Profiles will get created or updated according to the set anonymous ID.
   *
   * This method is intended to be used from server environments.
   */
  async upsertManyProfiles({
    events
  }, options = {}) {
    const requestName = 'Upsert Many Profiles';
    logger.info(`Sending ${requestName} request.`);
    const body = {
      events,
      options: this.constructOptions(options)
    };
    logger.debug(`${requestName} request Body: `, body);
    try {
      const response = await this.makeProfileMutationRequest(`/v2/organizations/${this.clientId}/environments/${this.environment}/events`, body, requestName, Object.assign({
        plainText: false
      }, options));
      const {
        data: {
          profiles
        }
      } = UpsertManyProfilesResponse.parse(await response.json());
      logger.debug(`${requestName} request successfully completed.`);
      return profiles;
    } catch (error) {
      this.logRequestError(error, {
        requestName
      });
      throw error;
    }
  }
  constructUrl(path, options) {
    const url = new URL(path, this.url);
    if (options.preflight) {
      url.searchParams.set('type', 'preflight');
    }
    if (options.locale) {
      url.searchParams.set('locale', options.locale);
    }
    return url.toString();
  }
  constructHeaders(options) {
    var _options$plainText;
    const headers = new Map();
    if (options.ip) {
      headers.set('X-Force-IP', options.ip);
    }
    if ((_options$plainText = options.plainText) != null ? _options$plainText : true) {
      headers.set('Content-Type', 'text/plain');
    } else {
      headers.set('Content-Type', 'application/json');
    }
    return Object.fromEntries(headers);
  }
}

class ConsoleLogSink {
  constructor() {
    this.name = 'ConsoleLogSink';
  }
  ingest(event) {
    const consoleLogMap = {
      debug: console.debug,
      info: console.info,
      log: console.log,
      warn: console.warn,
      error: console.error,
      fatal: console.error
    };
    const logFn = consoleLogMap[event.level];
    if (logFn) {
      logFn(...event.messages);
    }
  }
}

class OnLogLogSink {
  constructor(onLog) {
    this.name = 'OnLogLogSink';
    this.onLog = onLog;
  }
  ingest(event) {
    if (!event.messages.length) {
      return;
    }
    if (event.level == 'error' || event.level === 'fatal') {
      return;
    }
    if (typeof event.messages[0] !== 'string') {
      return;
    }
    this.onLog(event.messages[0], ...event.messages.slice(1));
  }
}

class OnErrorLogSink {
  constructor(onError) {
    this.name = 'OnErrorLogSink';
    this.onError = onError;
  }
  ingest(event) {
    if (!event.messages.length) {
      return;
    }
    if (event.level === 'error' || event.level === 'fatal') {
      if (event.messages[0] instanceof Error || typeof event.messages[0] === 'string') {
        this.onError(event.messages[0], ...event.messages.slice(1));
      }
    }
  }
}

const buildQuery = url => {
  const result = {};
  try {
    const parsedUrl = new URL(url);
    parsedUrl.searchParams.forEach((value, key) => {
      result[key] = value;
    });
  } catch (error) {
    //noop
  }
  return result;
};

const buildCampaign = ctx => {
  const params = buildQuery(ctx.url);
  const campaign = {};
  if (params['utm_campaign']) {
    campaign.name = params['utm_campaign'];
  }
  if (params['utm_source']) {
    campaign.source = params['utm_source'];
  }
  if (params['utm_medium']) {
    campaign.medium = params['utm_medium'];
  }
  if (params['utm_term']) {
    campaign.term = params['utm_term'];
  }
  if (params['utm_content']) {
    campaign.content = params['utm_content'];
  }
  return campaign;
};

const buildPage = ctx => {
  try {
    const query = buildQuery(ctx.url);
    const url = new URL(ctx.url);
    return {
      path: url.pathname,
      query,
      referrer: ctx.referrer,
      search: url.search,
      url: ctx.url
    };
  } catch (error) {
    return {
      path: '',
      query: {},
      referrer: '',
      search: '',
      url: ''
    };
  }
};

const buildEvent = ({
  messageId,
  timestamp,
  type,
  ctx,
  location
}) => {
  const date = new Date(timestamp).toISOString();
  return {
    channel: 'web',
    context: {
      library: {
        name: 'Ninetailed React Analytics SDK',
        version: "7.12.0" 
      },
      userAgent: ctx.userAgent,
      campaign: buildCampaign(ctx),
      locale: ctx.locale,
      page: buildPage(ctx),
      gdpr: {
        isConsentGiven: true
      },
      location
    },
    messageId: messageId,
    originalTimestamp: date,
    timestamp: date,
    sentAt: date,
    type
  };
};

const buildPageEvent = data => {
  var _data$ctx$document;
  return Object.assign({}, buildEvent(Object.assign({}, data, {
    type: 'page'
  })), {
    properties: Object.assign({}, data.properties, buildPage(data.ctx), {
      title: ((_data$ctx$document = data.ctx.document) == null ? void 0 : _data$ctx$document.title) || ''
    })
  });
};

const buildTrackEvent = data => {
  return Object.assign({}, buildEvent(Object.assign({}, data, {
    type: 'track'
  })), {
    event: data.event,
    properties: data.properties
  });
};

const buildIdentifyEvent = data => {
  return Object.assign({}, buildEvent(Object.assign({}, data, {
    type: 'identify'
  })), {
    traits: data.traits,
    userId: data.userId
  });
};

const buildComponentViewEvent = data => {
  return Object.assign({}, buildEvent(Object.assign({}, data, {
    type: 'component'
  })), {
    componentId: data.componentId,
    experienceId: data.experienceId,
    variantIndex: data.variantIndex
  });
};

const isBrowser = () => typeof window !== 'undefined';

const pipe = (...fns) => value => fns.reduce((acc, fn) => {
  return fn(acc);
}, value);

const unionBy = (array1, array2, prop) => {
  return Object.values([...array1, ...array2].reduce((acc, item) => {
    if (!(prop in acc)) {
      acc[item[prop]] = item;
    }
    return acc;
  }, {}));
};

const pickBy = (object, predicate) => {
  return Object.keys(object).reduce((acc, key) => {
    if (predicate(object[key], key)) {
      acc[key] = object[key];
    }
    return acc;
  }, {});
};

/**
 *
 * This function is a copy of the radash `template` function
 * https://github.com/rayepps/radash/blob/c378bd1bc401045ed7d8e5e47b275d2159ce43d5/src/string.ts#L118
 */
const template = (str, data, regex = /\{\{(.+?)\}\}/g) => {
  return Array.from(str.matchAll(regex)).reduce((acc, match) => {
    var _get;
    return acc.replace(match[0], (_get = get(data, match[1].trim())) != null ? _get : 'undefined');
  }, str);
};

const isPageViewEvent = event => {
  return event.type === 'page';
};
const isTrackEvent = event => {
  return event.type === 'track';
};
const isIdentifyEvent = event => {
  return event.type === 'identify';
};
const isComponentViewEvent = event => {
  return event.type === 'component';
};

const EXPERIENCE_TRAIT_PREFIX = 'nt_experiment_';

const LOWER_BOUND = 0;
const UPPER_BOUND = 4294967295;
const normalize = (val, min, max) => (val - min) / (max - min);
const getRandom = text => {
  const hash = murmur3(text, 0);
  const random = normalize(hash, LOWER_BOUND, UPPER_BOUND);
  return random;
};
const getTrafficRandom = (profile, experience) => getRandom(`traffic-${experience.id}-${profile.stableId}`);
const getDistributionRandom = (profile, experience) => getRandom(`distribution-${experience.id}-${profile.stableId}`);

const isExperienceMatch = ({
  experience,
  profile
}) => {
  const trafficRandom = getTrafficRandom(profile, experience);
  logger.info(`The traffic random factor for experience ${experience.id} is ${trafficRandom}. It's traffic allocation is set to ${experience.trafficAllocation}.`);
  const isInTrafficRange = experience.trafficAllocation > trafficRandom;
  const matchesAudience = !experience.audience || profile.audiences.includes(experience.audience.id);
  logger.info(`Is the profile in traffic allocation range? ${isInTrafficRange ? 'yes' : 'no'}.\n
      Does the profile match the audience of the experience? ${matchesAudience ? 'yes' : 'no'}.\n`);
  return isInTrafficRange && matchesAudience;
};

const pickExperimentTraits = profile => {
  const experimentTraits = {};
  for (const key in profile.traits) {
    if (key.startsWith(EXPERIENCE_TRAIT_PREFIX) && typeof profile.traits[key] === 'boolean' && profile.traits[key] === true) {
      experimentTraits[key] = profile.traits[key];
    }
  }
  return experimentTraits;
};
const selectActiveExperiments = (experiments, profile) => {
  const experimentTraits = pickExperimentTraits(profile);
  const experimentTraitsIds = Object.keys(experimentTraits).map(id => id.replace(EXPERIENCE_TRAIT_PREFIX, ''));
  const activeExperiments = experiments.filter(experiment => experimentTraitsIds.includes(experiment.id));
  return activeExperiments;
};

const selectBaselineWithVariants = (experience, baseline) => {
  var _experience$component;
  return (_experience$component = experience.components.find(baselineWithVariants => baselineWithVariants.baseline.id === baseline.id)) != null ? _experience$component : null;
};

const selectDistribution = ({
  experience,
  profile
}) => {
  const distributionRandom = getDistributionRandom(profile, experience);
  logger.log(`The distribution random factor for experience ${experience.id} is ${distributionRandom}. It's distribution is set to ${JSON.stringify(experience.distribution, null, 2)}.`);
  const distribution = experience.distribution.find(({
    start,
    end
  }) => distributionRandom >= start && distributionRandom <= end // this overlaps on one value for each boundary but we just find the first match
  );

  if (!distribution) {
    return {
      index: 0,
      start: 0,
      end: 1
    };
  }
  return distribution;
};

const selectExperience = ({
  experiences,
  profile
}) => {
  const selectedExperience = experiences.find(experience => isExperienceMatch({
    experience,
    profile
  }));
  return selectedExperience != null ? selectedExperience : null;
};

const selectVariants = (experience, baseline) => {
  const baselineWithVariants = selectBaselineWithVariants(experience, baseline);
  if (!baselineWithVariants) {
    return [];
  }
  return baselineWithVariants.variants;
};

const selectHasVariants = (experience, baseline) => {
  const variants = selectVariants(experience, baseline);
  return variants.length > 0;
};

const selectVariant = ({
  baseline,
  experience,
  profile
}) => {
  const variants = selectVariants(experience, baseline);
  const baselineVariant = Object.assign({}, baseline, {
    hidden: false
  });
  if (!variants.length) {
    return {
      variant: baselineVariant,
      index: 0
    };
  }
  const distribution = selectDistribution({
    experience,
    profile
  });
  if (!distribution) {
    return {
      variant: baselineVariant,
      index: 0
    };
  }
  if (distribution.index === 0) {
    return {
      variant: baselineVariant,
      index: 0
    };
  }
  const selectableVariants = [baselineVariant, ...variants];
  if (selectableVariants.length <= distribution.index) {
    logger.warn("A distribution for a variant was selected but it's metadata could not be found.");
    return {
      variant: baselineVariant,
      index: 0
    };
  }
  const variant = selectableVariants[distribution.index];
  return {
    variant,
    index: distribution.index
  };
};

export { ANONYMOUS_ID, Alias, Alpha2Code, CONSENT, Campaign, Change, ChangeTypes, Component, ConsoleLogSink, CreateProfileRequestBody, CreateProfileResponse, DEBUG_FLAG, EMPTY_MERGE_ID, EXPERIENCES_FALLBACK_CACHE, EXPERIENCE_TRAIT_PREFIX, FEATURES, GeoLocation, Group, Identify, Json, JsonLiteral, JsonLiteralObject, JsonObject, LEGACY_ANONYMOUS_ID, Logger, NINETAILED_ANONYMOUS_ID_COOKIE, NinetailedApiClient, OnErrorLogSink, OnLogLogSink, PROFILE_CHANGE, PROFILE_FALLBACK_CACHE, PROFILE_RESET, Page, Pageview, PageviewProperties, Profile, JsonObject as Properties, Query, RequestBodyOptions, Screen, SelectedVariantInfo, SessionStatistics, Track, JsonObject as Traits, UpdateProfileRequestBody, UpdateProfileResponse, UpsertManyProfilesRequestBody, UpsertManyProfilesResponse, VariableChange, buildCampaign, buildComponentViewEvent, buildEvent, buildIdentifyEvent, buildPage, buildPageEvent, buildQuery, buildTrackEvent, fetchTimeout, getDistributionRandom, getTrafficRandom, isBrowser, isComponentViewEvent, isExperienceMatch, isIdentifyEvent, isPageViewEvent, isTrackEvent, logger, pickBy, pickExperimentTraits, pipe, selectActiveExperiments, selectBaselineWithVariants, selectDistribution, selectExperience, selectHasVariants, selectVariant, selectVariants, template, unionBy };
